import { useState, useCallback, useEffect, useRef } from 'react';

interface InputData {
  industry: string;
  dailySessions: number;
  repeatRate: number;
  staffCount: number;
  workStart: string;
  workEnd: string;
}

interface ResultData {
  aiIndependentRate: [number, number];
  aiDesc: string;
  manualSessions: number;
  manualPercent: number;
  staffNeeded: [number, number];
  costDrop: number;
  costDetails: string[];
  satisfactionUp: [number, number];
}

const industries = [
  '电商/零售',
  '本地生活/服务预约',
  'SaaS/企业服务',
  '教育/培训',
  '金融/保险',
  '医疗/政务',
  '其他行业',
];

const caseA: InputData = {
  industry: 'SaaS/企业服务',
  dailySessions: 1600,
  repeatRate: 78,
  staffCount: 48,
  workStart: '09:00',
  workEnd: '22:00',
};

const caseB: InputData = {
  industry: '电商/零售',
  dailySessions: 1000,
  repeatRate: 70,
  staffCount: 30,
  workStart: '08:30',
  workEnd: '22:30',
};

const defaultInput: InputData = {
  industry: 'SaaS/企业服务',
  dailySessions: 10000,
  repeatRate: 50,
  staffCount: 20,
  workStart: '09:00',
  workEnd: '18:00',
};

const caseAResult: ResultData = {
  aiIndependentRate: [38, 51],
  aiDesc: 'AI完全独立解决大量标准化问题咨询',
  manualSessions: 800,
  manualPercent: 55,
  staffNeeded: [24, 30],
  costDrop: 34.8,
  costDetails: [
    '人员规模减少 → 招聘成本下降',
    '查账难度、人工排班算法改进 → 管理复杂度降低',
  ],
  satisfactionUp: [31.3, 31.3],
};

const caseBResult: ResultData = {
  aiIndependentRate: [50, 65],
  aiDesc: 'AI高效处理售前咨询与售后标准问题',
  manualSessions: 15200,
  manualPercent: 40,
  staffNeeded: [8, 12],
  costDrop: 82,
  costDetails: [
    '跨平台统一接待 → 运营成本大幅下降',
    '人员精简 → 管理与培训成本降低',
  ],
  satisfactionUp: [15, 35],
};

function calcResult(input: InputData): ResultData {
  const repeatFactor = input.repeatRate / 100;
  const baseAiRate = 30 + repeatFactor * 35;
  const aiLow = Math.min(Math.round(baseAiRate), 80);
  const aiHigh = Math.min(Math.round(baseAiRate + 15), 90);
  const manualPercent = 100 - aiHigh;
  const manualSessions = Math.round(input.dailySessions * (manualPercent / 100));
  const workHours = (() => {
    const [sh, sm] = input.workStart.split(':').map(Number);
    const [eh, em] = input.workEnd.split(':').map(Number);
    return eh + em / 60 - (sh + sm / 60);
  })();
  const sessionsPerStaffPerDay = Math.max(workHours * 15, 80);
  const staffLow = Math.max(Math.round((manualSessions / sessionsPerStaffPerDay) * 0.85), 1);
  const staffHigh = Math.max(Math.round((manualSessions / sessionsPerStaffPerDay) * 1.15), staffLow + 1);
  const costDrop = Math.min(Math.round((1 - staffHigh / Math.max(input.staffCount, 1)) * 100), 95);
  const satLow = Math.round(5 + repeatFactor * 15);
  const satHigh = Math.round(satLow + 20);
  return {
    aiIndependentRate: [aiLow, aiHigh],
    aiDesc: 'AI完全独立解决大量标准化问题咨询',
    manualSessions,
    manualPercent,
    staffNeeded: [staffLow, staffHigh],
    costDrop: Math.max(costDrop, 20),
    costDetails: ['人员规模减少 → 招聘成本下降', '管理复杂度降低 → 运营效率提升'],
    satisfactionUp: [Math.max(satLow, 5), Math.min(satHigh, 50)],
  };
}

function AnimatedNum({ value, suffix = '' }: { value: number; suffix?: string }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    animated.current = false;
    setDisplay(0);
  }, [value]);

  useEffect(() => {
    if (animated.current) {
      setDisplay(value);
      return;
    }
    const observer = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !animated.current) {
          animated.current = true;
          const start = performance.now();
          const dur = 900;
          const tick = (now: number) => {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(value * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return <span ref={ref}>{display}{suffix}</span>;
}

function Tooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex ml-1">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-[#0066ff] cursor-pointer transition-colors"
      >
        <i className="ri-question-line text-sm" />
      </button>
      {show && (
        <div className="absolute left-0 bottom-full mb-2 w-56 sm:w-64 p-2.5 sm:p-3 bg-gray-800 text-white text-[10px] sm:text-xs leading-relaxed rounded-lg shadow-xl z-50 whitespace-normal">
          {text}
          <div className="absolute left-4 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
}

function CostTooltip() {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex ml-0.5">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-amber-500 cursor-pointer transition-colors"
      >
        <i className="ri-question-line text-sm" />
      </button>
      {show && (
        <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-gray-800 text-white text-xs leading-relaxed rounded-lg shadow-xl z-50 text-left">
          客服人员规模缩减带来的管理人员人数下降、招聘人员数量下降、培训成本降低，绩效与考核工作量减少等
          <div className="absolute right-3 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
}

function TimePicker({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hour, minute] = value.split(':').map(Number);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleHourChange = (h: number) =>
    onChange(`${h.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
  const handleMinuteChange = (m: number) =>
    onChange(`${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:border-[#0066ff]/40 cursor-pointer"
      >
        <i className="ri-time-line text-gray-400" />
        <span>{value}</span>
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-xl z-30 p-3">
          <div className="text-xs text-gray-400 mb-2 text-center">{label}</div>
          <div className="flex gap-2">
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1 text-center">时</div>
              <div className="h-32 overflow-y-auto">
                {Array.from({ length: 24 }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => handleHourChange(i)}
                    className={`w-full py-1.5 text-sm rounded-md transition-colors cursor-pointer ${
                      hour === i
                        ? 'bg-gradient-to-r from-[#0066ff] to-[#5b4cff] text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {i.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1 text-center">分</div>
              <div className="h-32 overflow-y-auto">
                {[0, 15, 30, 45].map((m) => (
                  <button
                    key={m}
                    onClick={() => handleMinuteChange(m)}
                    className={`w-full py-1.5 text-sm rounded-md transition-colors cursor-pointer ${
                      minute === m
                        ? 'bg-gradient-to-r from-[#0066ff] to-[#5b4cff] text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {m.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-full mt-2 py-2 text-xs text-[#0066ff] font-medium hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
          >
            确定
          </button>
        </div>
      )}
    </div>
  );
}

type TabKey = 'caseA' | 'caseB' | 'custom';

export default function Calculator() {
  return <></>
  const [activeTab, setActiveTab] = useState<TabKey>('caseA');
  const [customInput, setCustomInput] = useState<InputData>({ ...defaultInput });
  const [customResult, setCustomResult] = useState<ResultData | null>(null);
  const [showContact, setShowContact] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [calculated, setCalculated] = useState(false);

  const currentInput: InputData =
    activeTab === 'caseA' ? caseA : activeTab === 'caseB' ? caseB : customInput;
  const currentResult: ResultData | null =
    activeTab === 'caseA' ? caseAResult : activeTab === 'caseB' ? caseBResult : customResult;

  const handleCalculate = useCallback(() => {
    try {
      setCustomResult(calcResult(customInput));
      setCalculated(true);
    } catch (e) {
      console.error('Calculation error:', e);
      setCustomResult(null);
      setCalculated(false);
    }
  }, [customInput]);

  const handleInputChange = useCallback(
    (patch: Partial<InputData>) => {
      if (activeTab !== 'custom') {
        const baseData = activeTab === 'caseA' ? caseA : caseB;
        setCustomInput({ ...baseData, ...patch });
        setActiveTab('custom');
      } else {
        setCustomInput((prev) => ({ ...prev, ...patch }));
      }
      setCalculated(false);
      setCustomResult(null);
    },
    [activeTab]
  );

  useEffect(() => {
    if (activeTab !== 'custom') setCalculated(false);
  }, [activeTab]);
  useEffect(() => {
    setDropdownOpen(false);
  }, [activeTab]);

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'caseA', label: '某知名SaaS企业', icon: 'ri-building-2-line' },
    { key: 'caseB', label: '某头部3C电商', icon: 'ri-shopping-bag-line' },
    { key: 'custom', label: '计算我的应用效果', icon: 'ri-calculator-line' },
  ];

  return (
    <section className="relative py-16 sm:py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#f0edff]/60 via-white to-[#eaf2ff]/40" />
      <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-[#0066ff]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#7c5cfc]/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 px-4">
            客户已经看到的
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0066ff] to-[#7c5cfc]">
              真实变化
            </span>
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 px-4">
            <i className="ri-information-line mr-1" />
            数据基于真实案例咨询统计，也可输入您的数据一键测算
          </p>
        </div>

        <div className="flex justify-center mb-6 sm:mb-8 px-4">
          <div className="inline-flex items-center bg-white rounded-full p-1 shadow-sm border border-gray-100 w-full sm:w-auto overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5 ${
                  activeTab === tab.key
                    ? 'bg-gradient-to-r from-[#0066ff] to-[#5b4cff] text-white shadow-md shadow-blue-200/50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${tab.icon} text-sm sm:text-base`} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">
                  {tab.label.replace('某知名', '').replace('某头部', '')}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* 主体：左右结构，items-stretch 保证等高 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          {/* 左侧：输入指标 */}
          <div className="flex flex-col">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/60 shadow-xl shadow-blue-100/20 overflow-hidden flex flex-col flex-1">
              <div className="px-5 sm:px-7 pt-5 pb-3 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#0066ff] to-[#5b4cff]">
                    <i className="ri-bar-chart-box-line text-white text-base" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">业务指标</h3>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {activeTab === 'custom'
                        ? '请输入您的业务数据'
                        : '客户真实业务数据（可直接修改）'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-5 sm:px-7 py-5 space-y-5 flex-1">
                {/* 行业 */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-1 h-3.5 bg-gradient-to-b from-[#0066ff] to-[#7c5cfc] rounded-full" />
                    所属行业
                  </h4>
                  <div className="relative">
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm transition-colors text-gray-700 hover:border-[#0066ff]/40 cursor-pointer"
                    >
                      <span>{currentInput.industry}</span>
                      <i
                        className={`ri-arrow-down-s-line text-gray-400 transition-transform ${
                          dropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {dropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-lg shadow-lg z-20 py-1 max-h-64 overflow-y-auto">
                        {industries.map((ind) => (
                          <button
                            key={ind}
                            onClick={() => {
                              handleInputChange({ industry: ind });
                              setDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors cursor-pointer ${
                              currentInput.industry === ind
                                ? 'text-[#0066ff] font-medium bg-blue-50/50'
                                : 'text-gray-600'
                            }`}
                          >
                            {ind}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* 日会话量 */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-1 h-3.5 bg-gradient-to-b from-[#0066ff] to-[#7c5cfc] rounded-full" />
                    日会话量
                  </h4>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={currentInput.dailySessions}
                      onChange={(e) =>
                        handleInputChange({
                          dailySessions: Math.max(0, Number(e.target.value)),
                        })
                      }
                      placeholder="请输入日会话量"
                      className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-semibold text-gray-800 outline-none transition-colors focus:border-[#0066ff]/50 focus:ring-2 focus:ring-[#0066ff]/10"
                    />
                    <span className="text-xs text-gray-400 whitespace-nowrap">个/日</span>
                  </div>
                </div>

                {/* 咨询重复度 */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-1 h-3.5 bg-gradient-to-b from-[#0066ff] to-[#7c5cfc] rounded-full" />
                    咒询重复度
                    <Tooltip text="咨询重复度即用户咨询问题的重复率，例如：78%的问题都是常见的「FAQ」类，22%是复杂问题，则咨询重复度为78%" />
                  </h4>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-400">低重复</span>
                    <span className="text-xs font-semibold text-gray-800">{currentInput.repeatRate}%</span>
                    <span className="text-xs text-gray-400">高重复</span>
                  </div>
                  <div className="relative h-6 flex items-center">
                    <div className="absolute inset-x-0 h-[6px] bg-gray-100 rounded-full" />
                    <div
                      className="absolute left-0 h-[6px] rounded-full"
                      style={{
                        width: `${currentInput.repeatRate}%`,
                        background: 'linear-gradient(90deg, #0066ff, #7c5cfc)',
                      }}
                    />
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={currentInput.repeatRate}
                      onChange={(e) => handleInputChange({ repeatRate: Number(e.target.value) })}
                      className="absolute inset-x-0 w-full h-6 opacity-0 cursor-pointer"
                    />
                    <div
                      className="absolute w-4 h-4 rounded-full bg-white border-2 border-[#0066ff] shadow-sm pointer-events-none"
                      style={{ left: `calc(${currentInput.repeatRate}% - 8px)` }}
                    />
                  </div>
                </div>

                {/* 客服人数 */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-1 h-3.5 bg-gradient-to-b from-[#0066ff] to-[#7c5cfc] rounded-full" />
                    客服人数
                  </h4>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={currentInput.staffCount}
                      onChange={(e) =>
                        handleInputChange({ staffCount: Math.max(1, Number(e.target.value)) })
                      }
                      placeholder="请输入客服人数"
                      className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-semibold text-gray-800 outline-none transition-colors focus:border-[#0066ff]/50 focus:ring-2 focus:ring-[#0066ff]/10"
                    />
                    <span className="text-xs text-gray-400 whitespace-nowrap">人</span>
                  </div>
                </div>

                {/* 客服上班时间 */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-1 h-3.5 bg-gradient-to-b from-[#0066ff] to-[#7c5cfc] rounded-full" />
                    客服上班时间
                  </h4>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <TimePicker
                        value={currentInput.workStart}
                        onChange={(v) => handleInputChange({ workStart: v })}
                        label="开始时间"
                      />
                    </div>
                    <span className="text-gray-300 text-sm">→</span>
                    <div className="flex-1">
                      <TimePicker
                        value={currentInput.workEnd}
                        onChange={(v) => handleInputChange({ workEnd: v })}
                        label="结束时间"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* 左侧底部按钮区域 — 与右侧底部对齐 */}
              <div className="px-5 sm:px-7 pb-5 sm:pb-7 mt-auto pt-3">
                <button
                  onClick={handleCalculate}
                  className="w-full py-3 text-white text-sm font-semibold rounded-xl transition-all whitespace-nowrap bg-gradient-to-r from-[#0066ff] to-[#5b4cff] hover:shadow-lg hover:shadow-blue-200/50 hover:scale-[1.02] cursor-pointer"
                >
                  <i className="ri-flashlight-line mr-2" />
                  立即测算效果
                </button>
              </div>
            </div>
          </div>

          {/* 右侧：效果预期 */}
          <div className="flex flex-col">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/60 shadow-xl shadow-blue-100/20 overflow-hidden flex flex-col flex-1">
              <div className="px-5 sm:px-7 pt-5 pb-3 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#0066ff] to-[#7c5cfc]">
                    <i className="ri-line-chart-line text-white text-base" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">预期效果</h3>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {activeTab === 'custom'
                        ? currentResult
                          ? '基于您的数据测算'
                          : '等待测算'
                        : '客户真实效果数据'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-5 sm:px-7 py-4 flex-1 flex flex-col">
                {activeTab === 'custom' && !currentResult ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center py-8">
                      <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#0066ff]/10 to-[#7c5cfc]/10">
                        <i className="ri-line-chart-line text-2xl text-[#0066ff]/50" />
                      </div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">
                        输入您的业务数据
                      </h4>
                      <p className="text-xs text-gray-300 px-4">
                        填写左侧指标后点击「立即测算效果」查看预期结果
                      </p>
                    </div>
                  </div>
                ) : currentResult ? (
                  <div className="space-y-3 flex-1">
                    {/* AI 接待情况 */}
                    <div className="bg-gradient-to-br from-[#0066ff]/5 to-[#7c5cfc]/5 rounded-xl p-3.5 border border-[#0066ff]/10">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-[#0066ff] to-[#5b4cff] rounded-xl shadow-lg shadow-blue-200/50 flex-shrink-0">
                            <i className="ri-robot-2-line text-white text-sm" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-bold text-gray-900 leading-tight">AI接待情况</h4>
                            <span className="text-xs text-gray-500 font-medium leading-tight block truncate">{currentResult.aiDesc}</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <div className="text-xs font-semibold text-gray-600 mb-0.5">AI独立接待占比</div>
                          <div className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#0066ff] to-[#7c5cfc] leading-tight">
                            <AnimatedNum value={currentResult.aiIndependentRate[0]} suffix="%" />
                            <span className="mx-1 text-gray-300 text-base">~</span>
                            <AnimatedNum value={currentResult.aiIndependentRate[1]} suffix="%" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 客服接待规模 */}
                    <div className="bg-gradient-to-br from-emerald-50/80 to-teal-50/50 rounded-xl p-3.5 border border-emerald-100/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl shadow-lg shadow-emerald-200/50 flex-shrink-0">
                            <i className="ri-team-line text-white text-sm" />
                          </div>
                          <h4 className="text-sm font-bold text-gray-900">客服接待规模</h4>
                        </div>
                        <div className="flex items-center gap-4">
                          {/* 人工接待会话量/日 */}
                          <div className="text-right">
                            <div className="text-xs font-semibold text-gray-600 mb-0.5">人工接待会话量/日</div>
                            <div className="flex items-baseline gap-1.5 justify-end">
                              <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500 leading-tight">
                                <AnimatedNum value={currentResult.manualSessions} />
                              </span>
                              <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                                仅{currentResult.manualPercent}%
                              </span>
                            </div>
                          </div>
                          <div className="w-px h-8 bg-emerald-100" />
                          {/* 实际所需客服人数 */}
                          <div className="text-right">
                            <div className="text-xs font-semibold text-gray-600 mb-0.5">实际所需客服人数</div>
                            <div className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500 leading-tight">
                              <AnimatedNum value={currentResult.staffNeeded[0]} />
                              <span className="mx-1 text-gray-300 text-base">~</span>
                              <AnimatedNum value={currentResult.staffNeeded[1]} />
                              <span className="text-sm font-medium text-gray-500 ml-0.5">人</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 管理成本 */}
                    <div className="bg-gradient-to-br from-amber-50/80 to-orange-50/50 rounded-xl p-3.5 border border-amber-100/50">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl shadow-lg shadow-amber-200/50 flex-shrink-0">
                            <i className="ri-money-dollar-circle-line text-white text-sm" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-bold text-gray-900 leading-tight">管理成本</h4>
                            <span className="text-xs text-gray-500 font-medium leading-tight block">管理团队人数及工作量下降</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <div className="flex items-center justify-end gap-0.5 mb-0.5">
                            <span className="text-xs font-semibold text-gray-600">管理成本下降</span>
                            <CostTooltip />
                          </div>
                          <div className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500 leading-tight">
                            35%~42%
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 客户满意度 */}
                    <div className="bg-gradient-to-br from-rose-50/80 to-pink-50/50 rounded-xl p-3.5 border border-rose-100/50">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl shadow-lg shadow-rose-200/50 flex-shrink-0">
                            <i className="ri-emotion-happy-line text-white text-sm" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-bold text-gray-900 leading-tight">客户满意度</h4>
                            <span className="text-xs text-gray-500 font-medium leading-tight block">响应速度与解决率双提升</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <div className="text-xs font-semibold text-gray-600 mb-0.5">满意度提升</div>
                          <div className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-pink-500 leading-tight">
                            <AnimatedNum value={currentResult.satisfactionUp[0]} suffix="%" />
                            <span className="mx-1 text-gray-300 text-base">~</span>
                            <AnimatedNum value={currentResult.satisfactionUp[1]} suffix="%" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* 右侧底部说明和按钮 */}
                <div className="mt-auto pt-3 space-y-2.5">
                  {currentResult && (
                    <>
                      <div className="bg-gray-50/80 rounded-lg px-3 py-2 border border-gray-100">
                        <p className="text-[10px] text-gray-400 leading-relaxed">
                          <i className="ri-information-line mr-1" />
                          本测算结果基于行业模型与公开数据，实际效果因业务场景差异可能不同
                        </p>
                      </div>
                      <button
                        onClick={() => setShowContact(true)}
                        className="w-full py-3 bg-gradient-to-r from-[#0066ff] to-[#5b4cff] text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-200/50 transition-all hover:scale-[1.02] cursor-pointer"
                      >
                        <i className="ri-mail-send-line mr-2" />
                        获取详细方案
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 联系弹窗 */}
      {showContact && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
          onClick={() => setShowContact(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm w-full mx-4"
            style={{ animation: 'scaleIn 0.2s ease-out' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-base font-semibold text-gray-800">联系我们</h3>
              <button
                onClick={() => setShowContact(false)}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
              <img
                src="https://static.readdy.ai/image/c9656b810d2c6cc0a8672cac16e7cf96/6504551e95a4474fdfc086d8d72e164f.png"
                alt="联系二维码"
                className="w-40 h-40 sm:w-48 sm:h-48 mx-auto object-cover"
              />
            </div>
            <p className="text-center text-xs text-gray-500 mt-3">
              使用微信扫描二维码联系我们
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
